import React, { useEffect, useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const C = {
  bg:     '#0d1117',
  card:   '#161b22',
  border: '#21262d',
  text:   '#e6edf3',
  muted:  '#8b949e',
  blue:   '#4a9eff',
  green:  '#36d399',
  orange: '#f6c90e',
  roed:   '#f87171',
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ color: C.muted, marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: <strong>
            {typeof p.value === 'number'
              ? p.dataKey === 'antal_lukkede' ? p.value : `${p.value} dage`
              : '—'}
          </strong>
        </div>
      ))}
    </div>
  );
}

export default function SagstidsTrendSR() {
  const [data, setData]       = useState([]);
  const [meta, setMeta]       = useState({});
  const [loading, setLoading] = useState(true);
  const [fejl, setFejl]       = useState(null);

  useEffect(() => {
    fetch('/api/os/strategisk/sagstid-trend', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.fejl) { setFejl(d.fejl); return; }
        setData(d.data || []);
        setMeta(d.meta || {});
      })
      .catch(e => setFejl(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 32, color: C.muted, textAlign: 'center' }}>Henter sagstidsdata...</div>;
  if (fejl)    return <div style={{ padding: 32, color: C.roed }}>Fejl: {fejl}</div>;

  // Vis tomdata-besked
  if (data.length === 0) return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '20px 16px', color: C.text, fontFamily: 'system-ui, sans-serif' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Sagstids-Trend</h2>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>Kompleksitet og behandlingstid for lukkede sager</div>
      <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: '32px 24px', textAlign: 'center', color: C.muted,
      }}>
        <div style={{ fontSize: 24, marginBottom: 12 }}>▦</div>
        <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>Ingen afsluttede sager i perioden</div>
        <div style={{ fontSize: 13 }}>Sagstidsdata vises naer sager begynder at blive arkiveret</div>
      </div>
    </div>
  );

  const maxDage = Math.max(...data.map(d => Math.max(d.gns_dage || 0, d.median_dage || 0)), 1);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '20px 16px', color: C.text, fontFamily: 'system-ui, sans-serif' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Sagstids-Trend</h2>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>Kompleksitet og behandlingstid for lukkede sager</div>

      {/* Meta KPI-kort */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total lukkede sager', vaerdi: (meta.total_lukkede || 0).toLocaleString('da-DK'), farve: C.blue   },
          { label: 'Gns. sagstid (alle)',  vaerdi: `${meta.gns_alle_dage || 0} dage`,                farve: C.orange },
          { label: 'Perioder vist',         vaerdi: `${data.length} maaneder`,                       farve: C.muted  },
        ].map(k => (
          <div key={k.label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 140,
          }}>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>{k.label}</div>
            <div style={{ color: k.farve, fontSize: 20, fontWeight: 700 }}>{k.vaerdi}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ width: '100%', height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="maaned" stroke={C.muted} tick={{ fontSize: 11 }} />
            <YAxis
              yAxisId="left"
              stroke={C.muted}
              tick={{ fontSize: 11 }}
              width={45}
              label={{ value: 'Dage', angle: -90, position: 'insideLeft', fill: C.muted, fontSize: 11 }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke={C.blue}
              tick={{ fontSize: 11 }}
              width={40}
              label={{ value: 'Sager', angle: 90, position: 'insideRight', fill: C.blue, fontSize: 11 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />

            {/* Antal lukkede sager (hoejre akse) */}
            <Bar
              yAxisId="right"
              dataKey="antal_lukkede"
              name="Lukkede sager"
              fill={C.blue}
              radius={[4, 4, 0, 0]}
              maxBarSize={36}
              fillOpacity={0.5}
            />

            {/* Gns. sagstid (vaenstre akse) */}
            <Line
              yAxisId="left"
              dataKey="gns_dage"
              name="Gns. sagstid"
              stroke={C.orange}
              strokeWidth={2}
              dot={{ r: 4, fill: C.orange }}
            />

            {/* Median sagstid (vaenstre akse) */}
            <Line
              yAxisId="left"
              dataKey="median_dage"
              name="Median sagstid"
              stroke={C.green}
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={{ r: 3, fill: C.green }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Maaned-tabel */}
      <div style={{ marginTop: 20, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 380 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['Maaned', 'Lukkede', 'Gns. dage', 'Median dage'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: C.muted, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr key={r.maaned} style={{ borderBottom: `1px solid ${C.border}22` }}>
                <td style={{ padding: '8px 12px', color: C.text }}>{r.maaned}</td>
                <td style={{ padding: '8px 12px', color: C.blue, fontWeight: 600 }}>{r.antal_lukkede}</td>
                <td style={{ padding: '8px 12px', color: C.orange }}>{r.gns_dage} dage</td>
                <td style={{ padding: '8px 12px', color: C.green }}>{r.median_dage} dage</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}